import {
  withFieldOrigin,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import type {
  FieldValue,
  FieldValues,
} from 'features/libra/ontology/ontologyFieldTypes'
import type { SavePartialField } from 'features/libra/sections/sectionTypes'
import { ZonesEditorSurface } from 'features/libra/stepComponents/zoneAnalysis/components/ZonesEditorSurface'
import {
  parseZonesFromFieldValues,
  reconcileZoneSnapshot,
  useStableZoneSnapshot,
  type ZoneChangeGuard,
  type ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { validateZoneGeometryChanges } from 'features/libra/stepComponents/zoneAnalysis/zoneGeometryValidation'
import {
  patchZoneById,
  removeZoneById,
  replaceImageZoneProposals,
} from 'features/libra/stepComponents/zoneAnalysis/zoneMutations'
import {
  latestZoneListOrigin,
  type ZoneOriginTracking,
} from 'features/libra/stepComponents/zoneAnalysis/zoneOriginTracking'
import {
  captureZoneSave,
  hasDuplicateStoredZoneIds,
  hasEmptyStoredZoneId,
  isZoneDirty as zoneIsDirty,
  prepareZoneFieldSave,
  reconcileZoneSaveSnapshot,
  reconcileZoneSnapshotOrigins,
  stageZoneSnapshot,
  type ZoneSnapshot,
} from 'features/libra/stepComponents/zoneAnalysis/zoneSave'
import type { FieldValuesSetter } from 'features/libra/ticket/fieldOrigin'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useTicketWriter } from 'features/libra/ticket/TicketWriterContext'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { RolodexPermissionsValues } from 'types'

import { ZonesSection } from './zonesSectionTypes'

const EmptyState = styled.div`
  padding: calc(var(--spacing) * 12);
  text-align: center;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasLegacyZoneRows(rawZones: unknown): boolean {
  return (
    Array.isArray(rawZones) &&
    rawZones.some((zone) => isRecord(zone) && zone.id === undefined)
  )
}

export interface ZonesSectionViewProps {
  config: ZonesSection
  fieldValues: FieldValues
  initialFieldValues?: FieldValues
  drawingResetKey?: number
  setFieldValues?: FieldValuesSetter
  savePartialField?: SavePartialField
}

export const ZonesSectionView: FC<ZonesSectionViewProps> = (props) => {
  const { ticketId } = useTicketAccess()
  const scopeKey = JSON.stringify([
    ticketId ?? null,
    props.config.zoneFieldName,
  ])
  const scopeRef = useRef<string | undefined>(scopeKey)
  scopeRef.current = scopeKey

  useEffect(() => {
    scopeRef.current = scopeKey
    return () => {
      if (scopeRef.current === scopeKey) scopeRef.current = undefined
    }
  }, [scopeKey])

  return (
    <ZonesSectionViewContent
      key={scopeKey}
      {...props}
      scopeKey={scopeKey}
      scopeRef={scopeRef}
    />
  )
}

const ZonesSectionViewContent: FC<
  ZonesSectionViewProps & {
    scopeKey: string
    scopeRef: MutableRefObject<string | undefined>
  }
> = ({
  config,
  fieldValues,
  initialFieldValues,
  drawingResetKey,
  setFieldValues,
  savePartialField,
  scopeKey,
  scopeRef,
}) => {
  const {
    ticketTags,
    readOnly: ticketReadOnly,
    isLoading: ticketLoading,
    ticketId,
  } = useTicketAccess()
  const { isSaving: isWriterSaving } = useTicketWriter()
  const unitId =
    typeof ticketTags.unit === 'string' ? ticketTags.unit : undefined
  const [isSaving, setIsSaving] = useState(false)
  const [savingZoneId, setSavingZoneId] = useState<string>()
  const [saveError, setSaveError] = useState<
    { zoneId: string; message: string } | undefined
  >()
  const savingRef = useRef(false)
  const isEditorLoading = ticketLoading && !isSaving && !isWriterSaving
  const readonly = !setFieldValues || ticketReadOnly || isEditorLoading
  const readonlyRef = useRef(readonly)
  readonlyRef.current = readonly

  const zoneFieldName = config.zoneFieldName
  const baselineSnapshot = useStableZoneSnapshot(
    initialFieldValues ?? fieldValues,
    zoneFieldName,
    ticketId,
    undefined,
    drawingResetKey
  )
  const persistedSnapshot = useStableZoneSnapshot(
    fieldValues,
    zoneFieldName,
    ticketId,
    baselineSnapshot,
    drawingResetKey
  )
  const persistedSnapshotRef = useRef(persistedSnapshot)
  persistedSnapshotRef.current = persistedSnapshot

  const currentSnapshotRef = useRef<ZoneSnapshot>({
    rawZones: persistedSnapshot.rawZones,
    zones: persistedSnapshot.zones,
  })
  const savedSnapshotRef = useRef<ZoneSnapshot>({
    rawZones: baselineSnapshot.rawZones,
    zones: baselineSnapshot.zones,
  })
  const originTrackingRef = useRef<ZoneOriginTracking>({
    zoneOrigins: new Map(),
    zoneMutationOrder: 0,
  })

  const persistedZones = useMemo(() => {
    return reconcileZoneSnapshot(
      currentSnapshotRef.current.rawZones,
      currentSnapshotRef.current.zones,
      persistedSnapshot.rawZones,
      persistedSnapshot.zones
    ).zones
  }, [persistedSnapshot])

  const parsedBaselineZones = useMemo(() => {
    return reconcileZoneSnapshot(
      savedSnapshotRef.current.rawZones,
      savedSnapshotRef.current.zones,
      baselineSnapshot.rawZones,
      baselineSnapshot.zones
    ).zones
  }, [baselineSnapshot])

  const baselineZones = useMemo(() => {
    return reconcileZoneSnapshot(
      persistedSnapshot.rawZones,
      persistedZones,
      baselineSnapshot.rawZones,
      parsedBaselineZones
    ).zones
  }, [
    baselineSnapshot.rawZones,
    parsedBaselineZones,
    persistedSnapshot.rawZones,
    persistedZones,
  ])

  const [drafts, setDrafts] = useState<ZoneParams[]>([])
  const displayed = useMemo(() => {
    const persistedIds = new Set(persistedZones.map((zone) => zone.id))
    const pendingDrafts = drafts.filter(
      (draft) => draft.ai_suggested && !persistedIds.has(draft.id)
    )
    return [...persistedZones, ...pendingDrafts]
  }, [persistedZones, drafts])

  useEffect(() => {
    const persistedIds = new Set(persistedZones.map((zone) => zone.id))
    setDrafts((current) => {
      const next = current.filter((zone) => !persistedIds.has(zone.id))
      return next.length === current.length ? current : next
    })
  }, [persistedZones])

  useLayoutEffect(() => {
    const currentSnapshot: ZoneSnapshot = {
      rawZones: persistedSnapshot.rawZones,
      zones: persistedZones,
    }
    const savedSnapshot: ZoneSnapshot = {
      rawZones: baselineSnapshot.rawZones,
      zones: baselineZones,
    }
    originTrackingRef.current = reconcileZoneSnapshotOrigins(
      originTrackingRef.current,
      currentSnapshotRef.current,
      currentSnapshot,
      savedSnapshotRef.current,
      savedSnapshot
    )
    currentSnapshotRef.current = currentSnapshot
    savedSnapshotRef.current = savedSnapshot
  }, [baselineSnapshot, baselineZones, persistedSnapshot, persistedZones])

  const commitReviewed = useCallback(
    (
      update: (zones: ZoneParams[]) => ZoneParams[],
      forceValidateIds: readonly string[] = []
    ) => {
      const currentSnapshot = persistedSnapshotRef.current
      if (
        !setFieldValues ||
        readonlyRef.current ||
        scopeRef.current !== scopeKey ||
        baselineSnapshot.parseError ||
        currentSnapshot.parseError ||
        hasDuplicateStoredZoneIds(currentSnapshot.rawZones) ||
        hasEmptyStoredZoneId(currentSnapshot.rawZones)
      )
        return

      setFieldValues((prev) => {
        if (scopeRef.current !== scopeKey) return prev
        const parsed = parseZonesFromFieldValues(prev, zoneFieldName)
        const currentRawZones = prev[zoneFieldName]?.value
        if (
          parsed.parseError ||
          baselineSnapshot.parseError ||
          hasDuplicateStoredZoneIds(currentRawZones) ||
          hasEmptyStoredZoneId(currentRawZones)
        )
          return prev
        const snapshot = persistedSnapshotRef.current
        if (
          snapshot.ticketId !== ticketId ||
          snapshot.fieldName !== zoneFieldName ||
          snapshot.resetKey !== drawingResetKey
        )
          return prev
        const reconciled = reconcileZoneSnapshot(
          currentSnapshotRef.current.rawZones,
          currentSnapshotRef.current.zones,
          currentRawZones,
          parsed.zones
        )
        if (
          reconciled.hasAmbiguousLegacyChange &&
          hasLegacyZoneRows(currentRawZones)
        )
          return prev
        const currentZones = reconciled.zones
        const nextZones = update(currentZones)
        if (
          isEqual(nextZones, currentZones) ||
          validateZoneGeometryChanges({
            baselineZones,
            proposedZones: nextZones,
            forceValidateIds,
          })
        )
          return prev

        const staged = stageZoneSnapshot(
          currentRawZones,
          currentZones,
          nextZones,
          originTrackingRef.current
        )
        if (!staged) return prev

        originTrackingRef.current = staged.originTracking
        currentSnapshotRef.current = staged.snapshot
        const currentWrapper = prev[zoneFieldName]
        const wrapper = omit(
          {
            ...(currentWrapper ?? wrapFieldValue(staged.snapshot.rawZones)),
            value: staged.snapshot.rawZones,
          },
          ['updateSource', 'updatedAt', 'aiExtracted', 'documentsSearched']
        )
        const listOrigin = latestZoneListOrigin(
          originTrackingRef.current.zoneOrigins
        )
        const nextWrapper =
          listOrigin === 'llm' ? withFieldOrigin(wrapper, listOrigin) : wrapper
        return { ...prev, [zoneFieldName]: nextWrapper }
      })
    },
    [
      baselineSnapshot,
      baselineZones,
      drawingResetKey,
      scopeKey,
      scopeRef,
      setFieldValues,
      ticketId,
      zoneFieldName,
    ]
  )

  const addZone = useCallback(
    (zone: ZoneParams) =>
      commitReviewed((zones) =>
        zones.some((item) => item.id === zone.id) ? zones : [...zones, zone]
      ),
    [commitReviewed]
  )

  const changeZone = useCallback(
    (zoneId: string, updates: Partial<ZoneParams>, guard?: ZoneChangeGuard) => {
      const update = (zones: ZoneParams[]) =>
        patchZoneById(zones, zoneId, updates, guard)
      if (readonlyRef.current) return
      setDrafts(update)
      commitReviewed(update)
    },
    [commitReviewed]
  )

  const removeZone = useCallback(
    (zoneId: string) => {
      if (readonlyRef.current) return
      const update = (zones: ZoneParams[]) => removeZoneById(zones, zoneId)
      setDrafts(update)
      commitReviewed(update)
    },
    [commitReviewed]
  )

  const confirmProposal = useCallback(
    (proposal: ZoneParams) => {
      if (readonlyRef.current) return
      commitReviewed(
        (zones) => {
          const current = zones.find((zone) => zone.id === proposal.id)
          if (current) {
            return current.ai_suggested
              ? zones.map((zone) =>
                  zone.id === proposal.id
                    ? { ...zone, ai_suggested: false }
                    : zone
                )
              : zones
          }
          return [...zones, { ...proposal, ai_suggested: false }]
        },
        [proposal.id]
      )
    },
    [commitReviewed]
  )

  const replaceImageProposals = useCallback(
    (imageId: string, proposals: ZoneParams[]) => {
      if (readonlyRef.current) return
      setDrafts((current) =>
        replaceImageZoneProposals(current, imageId, proposals)
      )
    },
    []
  )

  const isZoneDirty = useCallback(
    (zoneId: string) => zoneIsDirty(persistedZones, baselineZones, zoneId),
    [persistedZones, baselineZones]
  )

  const saveZone = useCallback(
    async (zoneId: string) => {
      if (savingRef.current)
        throw new Error('Another zone save is in progress.')
      if (!savePartialField || !setFieldValues || readonlyRef.current) {
        throw new Error('This zone cannot be saved right now.')
      }

      savingRef.current = true
      setIsSaving(true)
      setSavingZoneId(zoneId)
      setSaveError(undefined)

      try {
        const capture = captureZoneSave(
          currentSnapshotRef.current,
          savedSnapshotRef.current,
          originTrackingRef.current,
          zoneId
        )
        await savePartialField(
          zoneFieldName,
          (latest) => {
            const snapshot = persistedSnapshotRef.current
            if (
              readonlyRef.current ||
              scopeRef.current !== scopeKey ||
              snapshot.ticketId !== ticketId ||
              snapshot.fieldName !== zoneFieldName ||
              snapshot.resetKey !== drawingResetKey
            )
              throw new Error(
                'This zone can no longer be saved. Refresh and retry.'
              )
            return prepareZoneFieldSave(latest, capture)
          },
          (current, applied) => {
            if (scopeRef.current !== scopeKey) return current ?? applied
            const next = reconcileZoneSaveSnapshot({
              currentFieldExists: current !== undefined,
              currentRawZones: current?.value,
              appliedRawZones: applied.value,
              originTracking: originTrackingRef.current,
              scope: { kind: 'zone', capture },
            })
            let reconciled: FieldValue = {
              ...(current ?? applied),
              value: next.currentSnapshot.rawZones,
            }
            const listOrigin = latestZoneListOrigin(
              next.originTracking.zoneOrigins
            )
            if (listOrigin) {
              reconciled = withFieldOrigin(reconciled, listOrigin)
            } else if (applied.updateSource) {
              reconciled = withFieldOrigin(reconciled, applied.updateSource)
            } else {
              reconciled = omit(reconciled, 'updateSource')
            }
            originTrackingRef.current = next.originTracking
            currentSnapshotRef.current = next.currentSnapshot
            savedSnapshotRef.current = next.savedSnapshot
            return reconciled
          }
        )
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'The save failed. Retry.'
        setSaveError({ zoneId, message })
        throw error
      } finally {
        savingRef.current = false
        setIsSaving(false)
        setSavingZoneId(undefined)
      }
    },
    [
      drawingResetKey,
      savePartialField,
      scopeKey,
      scopeRef,
      setFieldValues,
      ticketId,
      zoneFieldName,
    ]
  )

  const getZoneSaveError = useCallback(
    (zoneId: string) =>
      saveError?.zoneId === zoneId ? saveError.message : undefined,
    [saveError]
  )

  if (!unitId) {
    return (
      <EmptyState>
        No unit specified for this ticket. Please ensure the ticket is tagged
        with a unit.
      </EmptyState>
    )
  }

  return (
    <ZonesEditorSurface
      zones={displayed}
      mutationBaselineZones={baselineZones}
      onAddZone={addZone}
      onChangeZone={changeZone}
      onRemoveZone={removeZone}
      onConfirmProposal={confirmProposal}
      onReplaceImageProposals={replaceImageProposals}
      onSaveZone={savePartialField ? saveZone : undefined}
      isZoneDirty={isZoneDirty}
      isSaving={isSaving || isWriterSaving}
      savingZoneId={savingZoneId}
      getZoneSaveError={getZoneSaveError}
      unitId={unitId}
      agentSectionId={config.id}
      zoneAgentPermissions={
        (config.agentPermissions ?? []) as RolodexPermissionsValues[]
      }
      visibleTabs={config.visibleTabs}
      showCoverageBreakdown={false}
      readonly={readonly}
      isLoading={isEditorLoading}
      drawingResetKey={drawingResetKey}
    />
  )
}
